/**
 * React Router の lazy ルート用ローダー。
 *
 * デプロイ直後の古いタブから lazy ルートへ遷移すると、ビルド時のハッシュ付きチャンク
 * (例: VideoEditorPage-abc123.js) が Vercel 上にもう無く、dynamic import が失敗する
 * ("Failed to fetch dynamically imported module" / MIME type エラー)。Router にそのまま
 * 投げると "Unexpected Application Error!" の素のエラー画面になり、ユーザーには
 * 「エディタを開いたらエラー」に見える (2026-09-09 に動画エディタで発生)。
 *
 * ここでは import 失敗を 1 回だけ「最新版へ更新中」表示 + ページリロードで吸収する。
 * リロード後も同じパスで失敗した場合は本当のエラーとして投げ直し、RouteErrorPage が表示する
 * (無限リロードを防ぐため、試行の有無を sessionStorage にパス単位で記録する)。
 */
import type { ComponentType } from 'react';
import { RouteReloadingPage } from '../components/routing/RouteStatusPages';

const STORAGE_KEY_PREFIX = 'lazy_route_reloaded:';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface LazyPageDeps {
  /** ページの再読み込み。 */
  reload: () => void;
  /** 現在のパス (リロード試行の記録キー)。 */
  pathname: () => string;
  /** 記録先。取得できない環境 (Safari プライベート等) では null を返し、その場合はリロードしない。 */
  storage: () => StorageLike | null;
  /** リロード待ちの間に表示するコンポーネント。 */
  ReloadingComponent: ComponentType;
}

type PageLoader = () => Promise<{ default: ComponentType }>;
/** RouteObject['lazy'] の関数形。 */
type LazyRoute = () => Promise<{ Component: ComponentType }>;

function readFlag(storage: StorageLike | null, key: string): boolean {
  try {
    return storage?.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(storage: StorageLike | null, key: string): boolean {
  try {
    if (!storage) return false;
    storage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

function clearFlag(storage: StorageLike | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    // 記録が残っても次回失敗時にリロードしないだけなので無視する
  }
}

export function createLazyPage(deps: LazyPageDeps) {
  return (loader: PageLoader): LazyRoute =>
    async () => {
      const storage = deps.storage();
      const key = STORAGE_KEY_PREFIX + deps.pathname();
      try {
        const mod = await loader();
        clearFlag(storage, key);
        return { Component: mod.default };
      } catch (error) {
        if (readFlag(storage, key)) {
          // リロード後も失敗 = 古いチャンクではなく本当のエラー。記録を消して上位に投げる
          clearFlag(storage, key);
          throw error;
        }
        if (!writeFlag(storage, key)) {
          // 記録できない環境でリロードすると無限ループになり得るため、そのままエラーにする
          throw error;
        }
        console.warn('[lazyRoute] ページの読み込みに失敗したため最新版へ更新します', error);
        deps.reload();
        return { Component: deps.ReloadingComponent };
      }
    };
}

function browserStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * 管理画面ページや動画エディタなど、初回バンドルから分離するルートに使う。
 * `element: <Page />` と同じ描画結果になるよう default export を Component として返す。
 */
export const lazyPage = createLazyPage({
  reload: () => window.location.reload(),
  pathname: () => window.location.pathname,
  storage: browserStorage,
  ReloadingComponent: RouteReloadingPage,
});
